import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ProgressBar from "@/components/base/ProgressBar";
import { activity } from "@/mocks/vivelo";
import { viveloTools } from "@/mocks/herramientas";
import { fetchAttentionItems, type AttentionItem } from "@/services/prospects";
import { fetchCurrentProfile } from "@/services/auth";
import { useGhlOverview } from "@/hooks/useGhlOverview";
import { useCourseProgress } from "@/hooks/useCourseProgress";
import { computeRouteModules, computeRouteStats, computeCoursesProgressAvg } from "@/lib/route";
import { fetchMyQuizResult } from "@/services/quiz";

const iconBgMap: Record<string, string> = {
  primary: "bg-primary-500/15 text-primary-600",
  accent: "bg-accent-500/15 text-accent-600",
  secondary: "bg-secondary-500/15 text-secondary-600",
};

const actionCards = [
  {
    key: "prospectar",
    emoji: "🔍",
    title: "Prospectar",
    desc: "Buscar y descubrir nuevos prospectos.",
    to: "/app/prospectar",
    color: "primary",
    icon: "ri-radar-line",
  },
  {
    key: "seguimiento",
    emoji: "👥",
    title: "Dar seguimiento",
    desc: "Ver prospectos que necesitan una acción.",
    to: "/app/contactos",
    color: "accent",
    icon: "ri-contacts-book-2-line",
  },
  {
    key: "aprender",
    emoji: "🎓",
    title: "Aprender",
    desc: "Continuar cursos y entrenamiento.",
    to: "/app/cursos",
    color: "secondary",
    icon: "ri-graduation-cap-line",
  },
  {
    key: "ruta",
    emoji: "🛤️",
    title: "Continuar mi ruta",
    desc: "Continúa tu camino personalizado.",
    to: "/app/mi-ruta",
    color: "primary",
    icon: "ri-route-line",
  },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [toolsOpen, setToolsOpen] = useState(false);
  const [challengeDone, setChallengeDone] = useState(false);
  const [attentionItems, setAttentionItems] = useState<AttentionItem[]>([]);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [attentionError, setAttentionError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("tú");
  const [profileLabel, setProfileLabel] = useState<string | null>(null);
  const { data: ghl, noContact } = useGhlOverview();
  const { progressByCourseId, loading: progressLoading } = useCourseProgress();

  useEffect(() => {
    let active = true;
    fetchCurrentProfile()
      .then((profile) => {
        if (active && profile?.fullName) setDisplayName(profile.fullName.split(" ")[0]);
      })
      .catch(() => {});

    fetchMyQuizResult()
      .then((result) => {
        if (active && result) setProfileLabel(result.profile.label);
      })
      .catch(() => {});

    fetchAttentionItems()
      .then((items) => {
        if (active) setAttentionItems(items);
      })
      .catch((err) => {
        if (active) setAttentionError(err instanceof Error ? err.message : "No se pudo cargar");
      })
      .finally(() => {
        if (active) setAttentionLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const visibleTools = toolsOpen ? viveloTools : viveloTools.slice(0, 3);

  const modules = computeRouteModules(progressByCourseId);
  const { completedModules, totalModules, routeProgress } = computeRouteStats(modules);
  const coursesProgress = !progressLoading ? computeCoursesProgressAvg(progressByCourseId) ?? 0 : 0;
  const streakDays = !noContact && ghl?.overview.habitsStreak ? Number(ghl.overview.habitsStreak) || 0 : 0;

  return (
    <AppShell headerVariant="onDark">
      {/* Hero navy */}
      <section className="bg-background-950 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-2 pb-16 relative">
          <div className="pointer-events-none absolute right-0 top-0 w-1/2 h-full opacity-30">
            <div className="absolute top-8 right-8 w-2 h-2 rounded-full bg-primary-400"></div>
            <div className="absolute top-24 right-40 w-1 h-1 rounded-full bg-accent-300"></div>
            <div className="absolute bottom-12 right-16 w-1.5 h-1.5 rounded-full bg-secondary-300"></div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-extrabold flex items-center gap-2">
                ¡Hola, {displayName}! <span aria-hidden="true">👋</span>
              </h1>
              <p className="text-foreground-200 mt-2 text-sm md:text-base">
                Este es tu centro de control. Aquí decides qué hacer hoy para avanzar.
              </p>
              <button
                onClick={() => navigate("/app/resultado")}
                className="mt-4 inline-flex items-center gap-2 bg-background-900/70 border border-accent-700/50 rounded-full pl-3 pr-4 py-2 text-sm cursor-pointer hover:border-primary-500 whitespace-nowrap"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500/20">
                  <i className="ri-brain-line text-accent-300"></i>
                </span>
                <span className="text-foreground-200">Tu perfil</span>
                <span className="vv-gradient-text font-semibold">{profileLabel ?? "Completa tu test"}</span>
                <i className="ri-arrow-right-s-line text-foreground-300"></i>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 self-start md:self-center w-full md:w-auto">
              <div className="rounded-2xl bg-background-900/70 border border-accent-700/40 px-4 py-3 text-center">
                <div className="text-primary-400 text-2xl">🔥</div>
                <div className="font-heading font-bold text-xl leading-tight">{streakDays} días</div>
                <div className="text-[11px] text-foreground-300">Racha activa</div>
              </div>
              <div className="rounded-2xl bg-background-900/70 border border-accent-700/40 px-4 py-3 text-center">
                <div className="text-accent-300 text-2xl"><i className="ri-route-line"></i></div>
                <div className="font-heading font-bold text-xl leading-tight">{routeProgress}%</div>
                <div className="text-[11px] text-foreground-300">Tu ruta</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content on light */}
      <section className="bg-background-50 -mt-8">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10">
          {/* ¿Qué quieres hacer hoy? */}
          <div className="-translate-y-4">
            <h2 className="font-heading font-bold text-foreground-950 text-lg mb-3">¿Qué quieres hacer hoy?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {actionCards.map((a) => (
                <button
                  key={a.key}
                  onClick={() => navigate(a.to)}
                  className="text-left bg-background-100 border border-background-200 rounded-2xl p-5 hover:border-primary-400 hover:-translate-y-0.5 cursor-pointer transition-all"
                >
                  <span className={`w-12 h-12 flex items-center justify-center rounded-xl ${iconBgMap[a.color]}`}>
                    <i className={`${a.icon} text-2xl`}></i>
                  </span>
                  <div className="mt-3 font-heading font-bold text-foreground-950 flex items-center gap-2">
                    <span aria-hidden="true">{a.emoji}</span> {a.title}
                  </div>
                  <p className="text-sm text-foreground-600 mt-1">{a.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
                    Empezar <i className="ri-arrow-right-line"></i>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Necesitan tu atención */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg inline-flex items-center gap-2">
                <i className="ri-alarm-warning-line text-accent-600"></i> Necesitan tu atención
              </h3>
              <button
                onClick={() => navigate("/app/contactos")}
                className="text-primary-600 hover:text-primary-700 text-sm inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                Ver CRM <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
            <div className="bg-background-100 border border-background-200 rounded-2xl divide-y divide-background-200 overflow-hidden">
              {attentionLoading ? (
                <div className="px-4 py-6 text-center text-sm text-foreground-500">
                  <i className="ri-loader-4-line animate-spin mr-2"></i> Cargando prospectos…
                </div>
              ) : attentionError ? (
                <div className="px-4 py-6 text-center text-sm text-accent-700">{attentionError}</div>
              ) : attentionItems.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-foreground-500">
                  No tienes prospectos pendientes por ahora.
                </div>
              ) : (
                attentionItems.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate("/app/contactos")}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-background-50 cursor-pointer text-left"
                  >
                    <img
                      src={a.photo}
                      alt={`Foto de ${a.name}`}
                      className="w-11 h-11 rounded-full object-cover object-top border border-background-200 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground-950 truncate">{a.name}</div>
                      <div className="text-xs text-foreground-600 truncate">{a.action}</div>
                    </div>
                    <span
                      className={[
                        "text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap",
                        a.tone === "urgent" ? "bg-accent-500/15 text-accent-700" : "bg-primary-500/15 text-primary-700",
                      ].join(" ")}
                    >
                      {a.when}
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-400"></i>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Herramientas Vívelo */}
          <div className="mt-6">
            <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg mb-3">Herramientas Vívelo</h3>
            <div key={toolsOpen ? "open" : "closed"} className="vv-anim-rise grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
              {visibleTools.map((t) => (
                <button
                  key={t.key}
                  onClick={() => navigate(t.to)}
                  className="bg-background-100 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-primary-400 cursor-pointer text-left transition-colors"
                >
                  <span className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${iconBgMap[t.color]}`}>
                    <i className={`${t.icon} text-xl`}></i>
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-foreground-950 truncate">{t.label}</div>
                    <div className="text-xs text-foreground-500 truncate">{t.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setToolsOpen((v) => !v)}
              className="mt-3 w-full py-2.5 rounded-xl border border-background-300 text-foreground-700 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:border-primary-400 cursor-pointer whitespace-nowrap transition-colors"
            >
              {toolsOpen ? "Ver menos herramientas" : "Ver todas las herramientas"}
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${toolsOpen ? "rotate-180" : ""}`}></i>
            </button>
          </div>

          {/* Progress */}
          <div className="mt-6 bg-background-100 rounded-2xl border border-background-200 p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg">Tu progreso general</h3>
              <button
                onClick={() => navigate("/app/mi-ruta")}
                className="text-primary-600 hover:text-primary-700 text-sm inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                Ver mi ruta <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ProgressCard icon="ri-route-line" color="accent" label="Mi Ruta" value={routeProgress} note="En progreso" />
              <ProgressCard icon="ri-book-open-line" color="primary" label="Cursos" value={coursesProgress} note={`${completedModules} de ${totalModules} completados`} />
              <ProgressCard icon="ri-star-line" color="warning" label="Masterclass" value={0} note="Pendiente" />
            </div>
          </div>

          {/* Reto + actividad */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-background-100 border border-background-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/15">
                  <i className="ri-focus-3-line text-primary-600"></i>
                </span>
                <h3 className="font-heading font-bold text-foreground-950">Reto de hoy</h3>
              </div>
              <div className="rounded-xl bg-primary-500/10 border border-primary-500/25 p-4 flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-sm text-foreground-800">
                    Aprueba y envía 3 openers generados por IA a tus prospectos mejor calificados.
                  </p>
                </div>
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/20">
                  <i className="ri-send-plane-line text-primary-600 text-lg"></i>
                </span>
              </div>
              <button
                onClick={() => setChallengeDone((v) => !v)}
                className={[
                  "mt-4 w-full py-3 rounded-xl font-heading font-semibold text-sm inline-flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-colors",
                  challengeDone
                    ? "bg-primary-500 text-background-950"
                    : "bg-primary-500/15 text-primary-700 hover:bg-primary-500/25",
                ].join(" ")}
              >
                {challengeDone ? "¡Completado hoy!" : "Marcar como completado"}
                <i className={challengeDone ? "ri-check-double-line" : "ri-check-line"}></i>
              </button>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/15">
                    <i className="ri-line-chart-line text-secondary-600"></i>
                  </span>
                  <h3 className="font-heading font-bold text-foreground-950">Actividad reciente</h3>
                </div>
                <button className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer whitespace-nowrap">
                  Ver todo
                </button>
              </div>
              <ul className="space-y-3">
                {activity.map((a) => (
                  <li key={a.id} className="flex items-center gap-3">
                    <span className={`w-10 h-10 flex items-center justify-center rounded-xl ${iconBgMap[a.color]}`}>
                      <i className={`${a.icon} text-lg`}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{a.title}</div>
                      <div className="text-xs text-foreground-500 truncate">{a.subtitle}</div>
                      {a.progress ? (
                        <div className="mt-1"><ProgressBar value={a.progress} color="accent" /></div>
                      ) : null}
                    </div>
                    {a.status === "done" ? (
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-950">
                        <i className="ri-check-line"></i>
                      </span>
                    ) : (
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </AppShell>
  );
}

function ProgressCard({
  icon,
  color,
  label,
  value,
  note,
}: {
  icon: string;
  color: "accent" | "primary" | "warning";
  label: string;
  value: number;
  note: string;
}) {
  const iconWrap =
    color === "accent"
      ? "bg-accent-500/15 text-accent-600"
      : color === "primary"
      ? "bg-primary-500/15 text-primary-600"
      : "bg-amber-100 text-amber-600";
  const valueColor =
    color === "accent" ? "text-accent-600" : color === "primary" ? "text-primary-600" : "text-amber-600";
  const barColor = color === "warning" ? "warning" : color;
  return (
    <div className="flex items-center gap-3">
      <span className={`w-12 h-12 flex items-center justify-center rounded-xl ${iconWrap}`}>
        <i className={`${icon} text-2xl`}></i>
      </span>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <span className="text-sm text-foreground-600">{label}</span>
          <span className={`font-heading font-extrabold text-lg ${valueColor}`}>{value}%</span>
        </div>
        <div className="mt-1"><ProgressBar value={value} color={barColor as "primary" | "accent" | "warning"} /></div>
        <div className="text-[11px] text-foreground-500 mt-1">{note}</div>
      </div>
    </div>
  );
}
